import { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Send, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const LeadForm = ({ variant = 'default' }) => {
  const { t, isRTL } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    business_name: '',
    contact_person: '',
    phone: '',
    business_type: '',
    city: '',
    message: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/leads`, formData);
      toast.success(t('leadForm.success'));
      setFormData({
        business_name: '',
        contact_person: '',
        phone: '',
        business_type: '',
        city: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting lead:', error);
      toast.error(t('leadForm.error'));
    } finally {
      setLoading(false);
    }
  };

  const inputClasses = "h-12 sm:h-14 px-4 sm:px-6 rounded-xl bg-slate-50 border-slate-200 focus:border-brand focus:ring-2 focus:ring-brand/20 transition-colors text-sm sm:text-base";
  const labelClasses = "text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2 block";

  return (
    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6" data-testid="lead-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        <div>
          <Label htmlFor="business_name" className={labelClasses}>
            {t('leadForm.businessName')} *
          </Label>
          <Input
            id="business_name"
            name="business_name"
            value={formData.business_name}
            onChange={handleChange}
            required
            className={inputClasses}
            data-testid="input-business-name"
            dir={isRTL ? 'rtl' : 'ltr'}
          />
        </div>
        <div>
          <Label htmlFor="contact_person" className={labelClasses}>
            {t('leadForm.contactPerson')} *
          </Label>
          <Input
            id="contact_person"
            name="contact_person"
            value={formData.contact_person}
            onChange={handleChange}
            required
            className={inputClasses}
            data-testid="input-contact-person"
            dir={isRTL ? 'rtl' : 'ltr'}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        <div>
          <Label htmlFor="phone" className={labelClasses}>
            {t('leadForm.phone')} *
          </Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            required
            className={inputClasses}
            data-testid="input-phone"
            dir="ltr"
          />
        </div>
        <div>
          <Label htmlFor="business_type" className={labelClasses}>
            {t('leadForm.businessType')} *
          </Label>
          <Input
            id="business_type"
            name="business_type"
            value={formData.business_type}
            onChange={handleChange}
            required
            className={inputClasses}
            data-testid="input-business-type"
            dir={isRTL ? 'rtl' : 'ltr'}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="city" className={labelClasses}>
          {t('leadForm.city')} *
        </Label>
        <Input
          id="city"
          name="city"
          value={formData.city}
          onChange={handleChange}
          required
          className={inputClasses}
          data-testid="input-city"
          dir={isRTL ? 'rtl' : 'ltr'}
        />
      </div>

      <div>
        <Label htmlFor="message" className={labelClasses}>
          {t('leadForm.message')}
        </Label>
        <Textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows={3}
          className="px-4 sm:px-6 py-3 sm:py-4 rounded-xl bg-slate-50 border-slate-200 focus:border-brand focus:ring-2 focus:ring-brand/20 transition-colors resize-none text-sm sm:text-base"
          data-testid="input-message"
          dir={isRTL ? 'rtl' : 'ltr'}
        />
      </div>

      <Button
        type="submit"
        disabled={loading}
        data-testid="submit-lead-form"
        className="w-full h-12 sm:h-14 rounded-full bg-brand hover:bg-brand/90 text-white text-base sm:text-lg font-semibold shadow-lg hover:shadow-xl transition-shadow duration-300"
      >
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <>
            <Send className={`w-4 h-4 sm:w-5 sm:h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
            {t('leadForm.submit')}
          </>
        )}
      </Button>
    </form>
  );
};
